import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/Tabs";
import { PageHeader } from "../components/layout/PageHeader";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { CreditCard, Calendar, Receipt, Check, Building2 } from "lucide-react";
import { fetchPlan, fetchPlans, fetchHistory } from "../store/actions/billingActions";
import * as billingServices from "../services/billingServices";

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  @media (min-width: ${({ theme }) => theme?.breakpoints?.md ?? 768}px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

/* Plan cards – same look as landing Pricing */
const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  max-width: 1152px;

  @media (min-width: ${({ theme }) => theme?.breakpoints?.md ?? 768}px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const PricingCard = styled.div`
  position: relative;
  padding: 32px;
  border-radius: 24px;
  background: ${({ theme, $popular }) =>
    $popular
      ? theme.colors?.light?.card?.background || theme.colors?.light?.background || "white"
      : theme.colors?.light?.card?.background || theme.colors?.light?.background || "white"};
  border: ${({ theme, $popular }) =>
    $popular
      ? `2px solid ${theme.colors?.light?.primary || theme.colors?.primary?.light || "#6366f1"}`
      : `1px solid ${theme.colors?.light?.border || "hsl(240, 10%, 90%)"}`};
  box-shadow: ${({ theme, $popular }) => ($popular ? theme.shadows?.glow : theme.shadows?.soft || "0 1px 3px rgba(0,0,0,0.08)")};
  transition: all 0.3s ease-out;

  body.dark & {
    background: ${({ theme }) => theme.colors?.dark?.card?.background || theme.colors?.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme, $popular }) =>
      $popular
        ? theme.colors?.dark?.primary || theme.colors?.primary?.light
        : theme.colors?.dark?.border || "hsl(240, 15%, 18%)"};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows?.lg || "0 10px 40px rgba(0,0,0,0.12)"};
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  padding: 6px 16px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors?.gradients?.primary || "linear-gradient(135deg, #6366f1, #8b5cf6)"};
  color: white;
  font-size: 14px;
  font-weight: 600;
`;

const PlanHeader = styled.div`
  margin-bottom: 24px;
`;

const PlanName = styled.h3`
  font-family: ${({ theme }) => theme.typography?.fontFamily?.display || "inherit"};
  font-size: ${({ theme }) => theme.typography?.fontSize?.h3 || "1.5rem"};
  font-weight: 700;
  margin: 0 0 8px 0;
  color: ${({ theme }) => theme.colors?.light?.foreground || theme.colors?.text?.heading || "#111827"};

  body.dark & {
    color: ${({ theme }) => theme.colors?.dark?.foreground || "#ffffff"};
  }
`;

const PlanDescription = styled.p`
  font-size: ${({ theme }) => theme.typography?.fontSize?.small || "0.875rem"};
  color: ${({ theme }) => theme.colors?.light?.muted?.foreground || theme.colors?.text?.muted || "#6b7280"};
  margin: 0;

  body.dark & {
    color: ${({ theme }) => theme.colors?.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const PriceDisplay = styled.div`
  margin-bottom: 24px;
`;

const PriceAmount = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 4px;
`;

const PriceValue = styled.span`
  font-family: ${({ theme }) => theme.typography?.fontFamily?.display || "inherit"};
  font-size: clamp(2.5rem, 4vw, 3rem);
  font-weight: 700;
  color: ${({ theme }) => theme.colors?.light?.foreground || theme.colors?.text?.heading || "#111827"};

  body.dark & {
    color: ${({ theme }) => theme.colors?.dark?.foreground || "#ffffff"};
  }
`;

const PricePeriod = styled.span`
  font-size: ${({ theme }) => theme.typography?.fontSize?.bodyLarge || "1.125rem"};
  color: ${({ theme }) => theme.colors?.light?.muted?.foreground || theme.colors?.text?.muted || "#6b7280"};

  body.dark & {
    color: ${({ theme }) => theme.colors?.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 24px 0 0 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: ${({ theme }) => theme.typography?.fontSize?.small || "0.875rem"};
  color: ${({ theme }) => theme.colors?.light?.foreground || theme.colors?.text?.heading || "#111827"};

  body.dark & {
    color: ${({ theme }) => theme.colors?.dark?.foreground || "#ffffff"};
  }
`;

const CheckCircle = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ theme }) => `${theme.colors?.light?.primary || theme.colors?.primary?.light || "#6366f1"}20`};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CheckIcon = styled(Check)`
  width: 12px;
  height: 12px;
  color: ${({ theme }) => theme.colors?.light?.primary || theme.colors?.primary?.light || "#6366f1"};

  body.dark & {
    color: ${({ theme }) => theme.colors?.dark?.primary || theme.colors?.primary?.light};
  }
`;

const PAYMENT_METHOD_LABELS = { card: "Card", bank_transfer: "Bank transfer" };
const CURRENCY_DEFAULT = "USD";
const INTERVAL_OPTIONS = [
  { value: "month", label: "Monthly" },
  { value: "quarter", label: "Quarterly" },
  { value: "half_year", label: "Half-yearly" },
  { value: "year", label: "Yearly" },
];

function formatBillingDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
}

function formatHistoryDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { dateStyle: "medium" });
  } catch {
    return "—";
  }
}

function priceDisplay(prices, currency = CURRENCY_DEFAULT, interval = "month") {
  const p = prices?.[currency];
  if (!p) return null;
  const amt = p[interval] ?? p.month ?? p.amount;
  if (amt == null) return null;
  const symbol = p.currency === "EUR" ? "€" : p.currency === "GBP" ? "£" : "$";
  return `${symbol}${(amt / 100).toFixed(2)}`;
}

const Billing = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { plan, plans, history, loading, error } = useSelector((state) => state.billing);
  const [tab, setTab] = useState("overview");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [billingCurrency, setBillingCurrency] = useState(CURRENCY_DEFAULT);
  const [billingInterval, setBillingInterval] = useState("month");
  const [bankTransferPlanId, setBankTransferPlanId] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);
  const [bankTransferLoading, setBankTransferLoading] = useState(false);
  const [bankTransferSubmitted, setBankTransferSubmitted] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [proofUploading, setProofUploading] = useState(false);
  const [proofUploaded, setProofUploaded] = useState(false);

  useEffect(() => {
    dispatch(fetchPlan());
    dispatch(fetchPlans());
    dispatch(fetchHistory());
  }, [dispatch]);

  useEffect(() => {
    if (!bankTransferPlanId || !billingCurrency) {
      setBankDetails(null);
      return;
    }
    setBankTransferSubmitted(null);
    setProofFile(null);
    setProofUploaded(false);
    let cancelled = false;
    billingServices.getBankDetails(billingCurrency, bankTransferPlanId, billingInterval).then((data) => {
      if (!cancelled) setBankDetails(data);
    }).catch(() => {
      if (!cancelled) setBankDetails(null);
    });
    return () => { cancelled = true; };
  }, [bankTransferPlanId, billingCurrency, billingInterval]);

  const currentPlanId = plan?.plan;
  const subscription = plan?.subscription;
  const nextBillingDate = subscription?.currentPeriodEnd;
  const paymentMethodLabel = subscription?.paymentMethod ? (PAYMENT_METHOD_LABELS[subscription.paymentMethod] || subscription.paymentMethod) : null;

  const paidPlans = (plans || []).filter((p) => p.id !== "free_trial" && p.prices);

  const handleSelectPlan = async (planId) => {
    if (currentPlanId === planId) return;
    setCheckoutLoading(true);
    try {
      const { url } = await billingServices.createCheckoutSession({ planId, currency: billingCurrency, interval: billingInterval });
      if (url) window.location.href = url;
    } catch (err) {
      console.error(err);
      setCheckoutLoading(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    setPortalLoading(true);
    try {
      const { url } = await billingServices.createPortalSession();
      if (url) window.location.href = url;
    } catch (err) {
      console.error(err);
      setPortalLoading(false);
    }
  };

  const handleBankTransferConfirm = async () => {
    if (!bankTransferPlanId || !billingCurrency) return;
    setBankTransferLoading(true);
    try {
      const res = await billingServices.bankTransferRequest({ planId: bankTransferPlanId, currency: billingCurrency, interval: billingInterval });
      setBankTransferSubmitted(res);
      setBankTransferLoading(false);
      dispatch(fetchHistory());
    } catch (err) {
      console.error(err);
      setBankTransferLoading(false);
    }
  };

  const handleProofUpload = async () => {
    if (!bankTransferSubmitted?.paymentId || !proofFile) return;
    setProofUploading(true);
    try {
      await billingServices.bankTransferProof(bankTransferSubmitted.paymentId, proofFile);
      setProofUploaded(true);
      setProofFile(null);
      dispatch(fetchHistory());
    } catch (err) {
      console.error(err);
    } finally {
      setProofUploading(false);
    }
  };

  return (
    <div className="animate-fadeIn">
      <PageHeader
        title={t("billing.title")}
        sub={t("billing.subtitle")}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">{t("billing.overview")}</TabsTrigger>
          <TabsTrigger value="plans">{t("billing.plans")}</TabsTrigger>
          <TabsTrigger value="history">{t("billing.paymentHistory")}</TabsTrigger>
          <TabsTrigger value="settings">{t("billing.settings")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div style={{ marginTop: 24 }}>
            <OverviewGrid>
              <Card style={{ padding: 20 }}>
                <h3 style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 600, color: "#6b7280" }}>{t("billing.currentPlan")}</h3>
                {loading && !plan ? (
                  <p style={{ color: "#6b7280" }}>{t("billing.loading")}</p>
                ) : error ? (
                  <p style={{ color: "#dc2626" }}>{error}</p>
                ) : plan ? (
                  <>
                    <p style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{plan.planName || plan.plan}</p>
                    {plan.isTrialing && plan.trialEndsAt && (
                      <p style={{ color: "#6b7280", margin: "4px 0 0", fontSize: 14 }}>
                        {t("billing.trialEnds", { date: formatBillingDate(plan.trialEndsAt) })}
                      </p>
                    )}
                    {!plan.isTrialing && plan.prices && (
                      <p style={{ color: "#6b7280", margin: "4px 0 0" }}>
                        {priceDisplay(plan.prices, subscription?.currency || CURRENCY_DEFAULT) ?? "—"} / {plan.interval || t("billing.perMonth")}
                      </p>
                    )}
                    {plan.features?.length > 0 && (
                      <ul style={{ margin: "12px 0 0", paddingLeft: 18, fontSize: 13, color: "#4b5563" }}>
                        {plan.features.slice(0, 3).map((f, i) => (
                          <li key={i} style={{ marginBottom: 2 }}>{f}</li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <p style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{t("billing.noActivePlan")}</p>
                )}
              </Card>
              <Card style={{ padding: 20 }}>
                <h3 style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 600, color: "#6b7280" }}>
                  <Calendar size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />
                  {t("billing.nextBillingDate")}
                </h3>
                <p style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{formatBillingDate(nextBillingDate)}</p>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>
                  {subscription?.status === "active" ? t("billing.yourNextChargeDate") : t("billing.setWhenPlanActive")}
                </p>
              </Card>
              <Card style={{ padding: 20 }}>
                <h3 style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 600, color: "#6b7280" }}>
                  <CreditCard size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />
                  {t("billing.paymentMethod")}
                </h3>
                {subscription?.paymentMethod ? (
                  <>
                    <p style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{paymentMethodLabel}</p>
                    <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>
                      {t("billing.manageInSettings")}
                    </p>
                  </>
                ) : (
                  <>
                    <p style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{t("billing.noneOnFile")}</p>
                    <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>{t("billing.addPaymentInSettings")}</p>
                  </>
                )}
              </Card>
            </OverviewGrid>
          </div>
        </TabsContent>

        <TabsContent value="plans">
          <div style={{ marginTop: 24 }}>
            <div style={{ marginBottom: 24, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 14, fontWeight: 500, color: "#6b7280" }}>{t("billing.currency")}</span>
              <select
                value={billingCurrency}
                onChange={(e) => setBillingCurrency(e.target.value)}
                style={{ padding: "8px 12px", borderRadius: 12, border: "1px solid #e5e7eb", background: "#fff" }}
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
              <span style={{ fontSize: 14, fontWeight: 500, color: "#6b7280", marginLeft: 8 }}>{t("billing.interval") || "Billing"}</span>
              <select
                value={billingInterval}
                onChange={(e) => setBillingInterval(e.target.value)}
                style={{ padding: "8px 12px", borderRadius: 12, border: "1px solid #e5e7eb", background: "#fff" }}
              >
                <option value="month">Monthly</option>
                <option value="quarter">Quarterly</option>
                <option value="half_year">Half-yearly</option>
                <option value="year">Yearly</option>
              </select>
              {bankTransferPlanId && (
                <button
                  type="button"
                  onClick={() => { setBankTransferPlanId(null); setBankDetails(null); setBankTransferSubmitted(null); }}
                  style={{ marginLeft: 8, fontSize: 13, color: "#6b7280", textDecoration: "underline" }}
                >
                  {t("common.close")}
                </button>
              )}
            </div>
            {bankTransferPlanId && (
              <Card style={{ padding: 24, marginBottom: 24 }}>
                <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                  <Building2 size={18} />
                  {t("billing.bankTransferDetails")}
                </h3>
                <p style={{ margin: "0 0 12px", fontSize: 14, color: "#6b7280" }}>
                  {t("billing.bankTransferSelectCurrency")}
                </p>
                {bankDetails ? (
                  bankTransferSubmitted ? (
                    <div style={{ padding: 16, background: "#f0fdf4", borderRadius: 12, border: "1px solid #bbf7d0" }}>
                      <p style={{ margin: 0, fontWeight: 600, color: "#15803d" }}>{t("billing.bankTransferPending")}</p>
                      <p style={{ margin: "8px 0 0", fontSize: 14, color: "#166534" }}>
                        {t("billing.bankTransferReference")}: <strong>{bankTransferSubmitted.reference}</strong>
                      </p>
                      <p style={{ margin: "4px 0 0", fontSize: 13, color: "#166534" }}>{t("billing.bankTransferActivateNote")}</p>
                      {!proofUploaded ? (
                        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #bbf7d0" }}>
                          <p style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 600 }}>{t("billing.uploadProof")}</p>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                            <input
                              type="file"
                              accept="image/*,application/pdf"
                              onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                              style={{ fontSize: 14 }}
                            />
                            <Button onClick={handleProofUpload} disabled={!proofFile || proofUploading} $size="sm">
                              {proofUploading ? t("billing.loading") : t("billing.uploadProofButton")}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p style={{ margin: "12px 0 0", fontSize: 14, color: "#15803d" }}>{t("billing.proofUploaded")}</p>
                      )}
                    </div>
                  ) : (
                    <>
                      <div style={{ display: "grid", gap: 12, marginBottom: 16, fontSize: 14 }}>
                        {bankDetails.amount && (
                          <p style={{ margin: 0, fontWeight: 600 }}>{t("billing.amount")}: {bankDetails.currency === "EUR" ? "€" : bankDetails.currency === "GBP" ? "£" : "$"}{bankDetails.amount}</p>
                        )}
                        <p style={{ margin: 0 }}><strong>{t("billing.accountName")}</strong>: {bankDetails.accountName}</p>
                        <p style={{ margin: 0 }}><strong>{t("billing.bankName")}</strong>: {bankDetails.bankName}</p>
                        {bankDetails.iban && <p style={{ margin: 0 }}><strong>IBAN</strong>: {bankDetails.iban}</p>}
                        {bankDetails.accountNumber && !bankDetails.iban && <p style={{ margin: 0 }}><strong>{t("billing.accountNumber")}</strong>: {bankDetails.accountNumber}</p>}
                        {bankDetails.swiftBic && <p style={{ margin: 0 }}><strong>Swift/BIC</strong>: {bankDetails.swiftBic}</p>}
                        {bankDetails.instructions && <p style={{ margin: 0, whiteSpace: "pre-wrap", color: "#6b7280" }}>{bankDetails.instructions}</p>}
                      </div>
                      <Button onClick={handleBankTransferConfirm} disabled={bankTransferLoading}>
                        {bankTransferLoading ? t("billing.loading") : t("billing.confirmBankTransfer")}
                      </Button>
                    </>
                  )
                ) : (
                  <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>{t("billing.loading")}</p>
                )}
              </Card>
            )}
            <PlansGrid>
              {paidPlans.length === 0 && !loading && (
                <Card style={{ padding: 24, gridColumn: "1 / -1" }}>
                  <p style={{ color: "#6b7280" }}>{t("billing.noPaidPlans")}</p>
                </Card>
              )}
              {paidPlans.map((p) => {
                const price = priceDisplay(p.prices, billingCurrency, billingInterval);
                const isCurrent = currentPlanId === p.id;
                const isPopular = p.id === "pro";
                const periodLabel = billingInterval === "month" ? t("billing.perMonth") : billingInterval === "quarter" ? "/quarter" : billingInterval === "half_year" ? "/6 months" : "/year";
                return (
                  <PricingCard key={p.id || p._id} $popular={isPopular}>
                    {isPopular && <PopularBadge>{t("billing.mostPopular")}</PopularBadge>}
                    <PlanHeader>
                      <PlanName>{p.name || p.planName}</PlanName>
                      <PlanDescription>{p.description || `Up to ${p.limits?.stores === -1 ? "unlimited" : p.limits?.stores || "—"} stores`}</PlanDescription>
                    </PlanHeader>
                    <PriceDisplay>
                      <PriceAmount>
                        <PriceValue>{price ?? "—"}</PriceValue>
                        <PricePeriod>/{periodLabel}</PricePeriod>
                      </PriceAmount>
                    </PriceDisplay>
                    <Button
                      $variant={isCurrent ? "outline" : isPopular ? "hero" : "outline"}
                      fullWidth
                      $size="lg"
                      style={{ marginBottom: 8 }}
                      disabled={isCurrent || checkoutLoading}
                      onClick={() => handleSelectPlan(p.id)}
                    >
                      {isCurrent ? t("billing.currentPlanButton") : t("billing.selectPlan")}
                    </Button>
                    {!isCurrent && (
                      <button
                        type="button"
                        onClick={() => setBankTransferPlanId(p.id)}
                        style={{ background: "none", border: "none", padding: 0, fontSize: 13, color: "#6b7280", cursor: "pointer", marginBottom: 8, textDecoration: "underline" }}
                      >
                        {t("billing.payByBankTransfer")}
                      </button>
                    )}
                    {p.features?.length > 0 && (
                      <FeaturesList>
                        {p.features.map((f, i) => (
                          <FeatureItem key={i}>
                            <CheckCircle>
                              <CheckIcon size={12} />
                            </CheckCircle>
                            <span>{f}</span>
                          </FeatureItem>
                        ))}
                      </FeaturesList>
                    )}
                  </PricingCard>
                );
              })}
            </PlansGrid>
          </div>
        </TabsContent>

        <TabsContent value="history">
          <Card style={{ padding: 24, marginTop: 24 }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
              <Receipt size={18} style={{ verticalAlign: "middle", marginRight: 6 }} />
              {t("billing.paymentHistory")}
            </h2>
            {loading && history.length === 0 ? (
              <p style={{ color: "#6b7280" }}>{t("billing.loading")}</p>
            ) : error ? (
              <p style={{ color: "#dc2626" }}>{error}</p>
            ) : history.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: 20, listStyle: "none" }}>
                {history.map((h) => {
                  const amountStr = h.currency && h.amount != null
                    ? `${h.currency === "USD" ? "$" : h.currency === "EUR" ? "€" : "£"}${(h.amount / 100).toFixed(2)}`
                    : h.amount != null ? (h.amount / 100).toFixed(2) : "";
                  return (
                    <li key={h.id || h._id} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid #f3f4f6" }}>
                      <span style={{ fontWeight: 500 }}>{formatHistoryDate(h.date)}</span>
                      {amountStr && <span style={{ marginLeft: 8 }}> – {amountStr}</span>}
                      {h.description && <span style={{ color: "#6b7280", marginLeft: 8 }}>{h.description}</span>}
                      {h.status && <span style={{ marginLeft: 8, fontSize: 13, color: "#6b7280" }}>{` (${h.status})`}</span>}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p style={{ color: "#6b7280" }}>{t("billing.noPaymentHistoryYet")}</p>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 24 }}>
            <Card style={{ padding: 24 }}>
              <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 600 }}>{t("billing.billingInformation")}</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 400 }}>
                <Input placeholder={t("billing.companyName")} />
                <Input placeholder={t("billing.taxIdOptional")} />
                <Input placeholder={t("billing.billingAddress")} />
              </div>
            </Card>
            <Card style={{ padding: 24 }}>
              <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 600 }}>{t("billing.paymentDetails")}</h3>
              <p style={{ margin: 0, fontSize: 14, color: "#6b7280" }}>{t("billing.updatePaymentDesc")}</p>
              <Button
                $variant="outline"
                style={{ marginTop: 12 }}
                disabled={portalLoading}
                onClick={handleUpdatePaymentMethod}
              >
                <CreditCard size={16} style={{ marginRight: 6, verticalAlign: "middle" }} />
                {portalLoading ? t("billing.opening") : t("billing.updatePaymentMethod")}
              </Button>
            </Card>
            <Card style={{ padding: 24 }}>
              <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 600 }}>{t("billing.notificationPreferences")}</h3>
              <p style={{ margin: 0, fontSize: 14, color: "#6b7280" }}>{t("billing.receiveEmailInvoices")}</p>
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
                <input type="checkbox" defaultChecked />
                <span style={{ fontSize: 14 }}>{t("billing.emailInvoicesReceipts")}</span>
              </label>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Billing;
